// store/slices/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios'
import UserService from '@/services/users';


interface Permissions {
  can_add: boolean;
  can_delete: boolean;
  can_edit: boolean;
  can_view: boolean;
  category: string;
  feature: string;
  module: string;
  parent_id: number;
  permission_id: number;
  role_name: string;
}

export interface UserPermissions {
  permissions: Permissions[],
  success: boolean,
  totalpermissions: number
}

interface UserPermissionsState {
  data: UserPermissions | null;
  loading: boolean;
  error: string | null;
  fetched: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  errorCode?: number;
  errorDetails?: unknown;

}

const initialState: UserPermissionsState = {
  data: null,
  loading: false,
  error: null,
  fetched: false,
  status: 'idle',
  errorCode: undefined,
  errorDetails: undefined,

};

type ThunkArg = {
  userId: string;
  accessToken?: string
};

type RejectedPayload = {
  message: string;
  status?: number;
  details?: unknown;
};


/**
 * Normalize Axios error to {message, status, details}.
 */
function toRejectedPayload(err: unknown): RejectedPayload {
  const fallback: RejectedPayload = { message: 'Unknown error' };
  if (!err || typeof err !== 'object') return fallback;

  const axiosErr = err as AxiosError<any>;
  // Axios error shape: response?.status, response?.data, message
  const message =
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Request failed';

  // console.log('toRejectedPayload: extracted error message', axiosErr.toJSON?.());
  const status = axiosErr.response?.status;
  // const details = axiosErr.response?.data ?? axiosErr.toJSON?.() ?? undefined;
  const details = {
    name: axiosErr.name,
    code: axiosErr.code,
    message: axiosErr.message,
    status: axiosErr.response?.status,
    // config: axiosErr.config,
    responseData: axiosErr.response?.data,
  };
  
  return { message, status, details };
}

// Async thunk for fetching user data
export const fetchUserPermissions = createAsyncThunk<UserPermissions, ThunkArg,{ rejectValue: RejectedPayload }>
('user/fetchUserPermissions', async ({userId, accessToken}, { rejectWithValue }) => {
    try {
      console.log('fetchUserPermissions: starting fetch',userId);
      
      const userService = new UserService();
      const userDetails = await userService.getPermissions(userId);
      console.log('fetchUserPermissions: fetched permissions', userDetails);
      
      // return userDetails;
      return {
        permissions: userDetails.permissions,
        totalpermissions: userDetails.total_permissions
      } as UserPermissions;
    } catch (err) {
      return rejectWithValue(toRejectedPayload(err));
    }
    
  },
  {
    // condition to prevent duplicate requests
    condition: (_arg, { getState }) => {
      const state = getState() as { userpermissions: UserPermissionsState };
           return state.userpermissions.status === 'idle' && !state.userpermissions.fetched;
    },
  }

);

const userPermissionsSlice = createSlice({
  name: 'userPermissions',
  initialState,
  reducers: {
    clearUser: (state) => {
      state.loading = false;
      state.data = null;
      state.error = null;
      state.fetched = false; // Reset fetched flag
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPermissions.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        const payloadData = action.payload;
        console.log('userPermissionSlice: fetched user', payloadData);
        state.loading = false;
        state.data = payloadData as UserPermissions;
        state.fetched = true; // Mark as fetched
        state.status = 'succeeded';
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        // state.error = action.error.message || 'Failed to fetch user';
        state.fetched = true; // Still mark as fetched to prevent retries

        const payload = action.payload as RejectedPayload | undefined;
        if (payload) {
          state.error = payload.message;
          state.errorCode = payload.status || 99;
          state.errorDetails = payload.details;
        } else {
          // Fallback to RTK's generic error
          state.error = action.error.message || 'Failed to fetch user';
        }

        // Optional: special handling for 401/403
        // if (state.errorCode === 401 || state.errorCode === 403) {
        //   // e.g., dispatch(logout) or mark a flag to trigger re-auth UI
        // }

      });
  },
});

export const { clearUser } = userPermissionsSlice.actions;
export default userPermissionsSlice.reducer;